import { Head, router } from '@inertiajs/react'
import dayjs, { Dayjs } from 'dayjs'
import {
  CalendarOutlined,
  CheckCircleOutlined,
  MailOutlined,
  PercentageOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { DatePicker, Select, Statistic, Tag, Typography } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import AdminLayout from '@/components/layouts/AdminLayout'
import { PageHeader } from '@/components/patterns'
import { Button, Card, Col, Row, Table } from '@/components/ui'

const { Text } = Typography

type AdminUser = {
  id: number
  email: string
  name?: string | null
  role: string
}

type Summary = {
  grand_total: number
  sent_last_30_days: number
  total_failed: number
  overall_acceptance_rate: number
  filtered_accepted_count: number
  filtered_attempt_count: number
}

type Filters = {
  from_date?: string
  to_date?: string
  event_id?: string
  email_type?: string
  status?: string
}

type FilterOption = {
  label: string
  value: string
}

type FilterOptions = {
  events: FilterOption[]
  email_types: FilterOption[]
  statuses: FilterOption[]
}

type EventSummary = {
  event_id: number | null
  event_name: string
  accepted_count: number
  failed_count: number
  attempt_count: number
  last_attempted_at: string | null
}

type TypeSummary = {
  email_type: string
  email_type_label: string
  accepted_count: number
  failed_count: number
  attempt_count: number
}

type RecentActivity = {
  id: number
  attempted_at: string
  event_name: string
  email_type: string
  email_type_label: string
  recipient_email: string
  sender: string
  subject: string | null
  status: 'accepted' | 'failed'
  detail: string | null
}

type EmailActivityProps = {
  user: AdminUser
  summary: Summary
  filters: Filters
  filter_options: FilterOptions
  per_event: EventSummary[]
  per_type: TypeSummary[]
  recent_activity: RecentActivity[]
}

function formatDateTime(value: string | null) {
  if (!value) return '—'

  const date = dayjs(value)
  return date.isValid() ? date.format('MMM D, YYYY h:mm A') : value
}

export default function EmailActivityPage({
  summary,
  filters,
  filter_options,
  per_event,
  per_type,
  recent_activity,
}: EmailActivityProps) {
  const [draftFilters, setDraftFilters] = useState<Filters>(filters)

  useEffect(() => {
    setDraftFilters(filters)
  }, [filters])

  const selectedDateRange = useMemo<[Dayjs, Dayjs] | null>(() => {
    if (!draftFilters.from_date || !draftFilters.to_date) return null

    return [dayjs(draftFilters.from_date), dayjs(draftFilters.to_date)]
  }, [draftFilters.from_date, draftFilters.to_date])

  const applyFilters = () => {
    const query = Object.fromEntries(
      Object.entries(draftFilters).filter(([, value]) => value !== undefined && value !== ''),
    )

    router.get('/admin/email_activity', query, {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    })
  }

  const clearFilters = () => {
    setDraftFilters({})
    router.get('/admin/email_activity', {}, { preserveScroll: true, replace: true })
  }

  return (
    <AdminLayout>
      <Head title="Email Activity" />

      <div className="w-full bg-[#f5f5f5]">
        <PageHeader
          title="Email Activity"
          description="Track every email request accepted by the delivery service across all events."
        />

      <div className="flex flex-col gap-4 p-6">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="h-full border-t-2 border-t-[#132B51]">
                <Statistic
                  title="Grand Total Sent"
                  value={summary.grand_total}
                  prefix={<MailOutlined />}
                />
                <Text type="secondary">All-time accepted emails</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="h-full border-t-2 border-t-[#2B65B0]">
                <Statistic
                  title="Sent in Last 30 Days"
                  value={summary.sent_last_30_days}
                  prefix={<CalendarOutlined />}
                />
                <Text type="secondary">Rolling 30-day window</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="h-full border-t-2 border-t-[#CF1322]">
                <Statistic
                  title="Failed Attempts"
                  value={summary.total_failed}
                  valueStyle={{ color: '#CF1322' }}
                  prefix={<WarningOutlined />}
                />
                <Text type="secondary">All-time rejected or errored</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="h-full border-t-2 border-t-[#389E0D]">
                <Statistic
                  title="Acceptance Rate"
                  value={summary.overall_acceptance_rate}
                  precision={1}
                  suffix="%"
                  prefix={<PercentageOutlined />}
                />
                <Text type="secondary">Accepted ÷ all attempts</Text>
              </Card>
            </Col>
          </Row>

          <Card
            title="Filter Activity"
            extra={
              <Text type="secondary">
                {summary.filtered_accepted_count.toLocaleString()} sent from{' '}
                {summary.filtered_attempt_count.toLocaleString()} attempts
              </Text>
            }
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
              <DatePicker.RangePicker
                value={selectedDateRange}
                format="YYYY-MM-DD"
                placeholder={['From date', 'To date']}
                onChange={(dates) => {
                  setDraftFilters((current) => ({
                    ...current,
                    from_date: dates?.[0]?.format('YYYY-MM-DD'),
                    to_date: dates?.[1]?.format('YYYY-MM-DD'),
                  }))
                }}
                className="w-full"
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="All events"
                value={draftFilters.event_id}
                options={filter_options.events}
                onChange={(value) => setDraftFilters((current) => ({ ...current, event_id: value }))}
              />
              <Select
                allowClear
                placeholder="All email types"
                value={draftFilters.email_type}
                options={filter_options.email_types}
                onChange={(value) => setDraftFilters((current) => ({ ...current, email_type: value }))}
              />
              <Select
                allowClear
                placeholder="All statuses"
                value={draftFilters.status}
                options={filter_options.statuses}
                onChange={(value) => setDraftFilters((current) => ({ ...current, status: value }))}
              />
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={clearFilters}>
                  Clear
                </Button>
                <Button variant="primary" onClick={applyFilters}>
                  Apply Filters
                </Button>
              </div>
            </div>
          </Card>

          <Row gutter={[16, 16]}>
            <Col xs={24} xl={16}>
              <Card
                title="Sent per Event"
                extra={<Text type="secondary">Includes system emails not tied to an event</Text>}
                className="h-full"
              >
                <Table<EventSummary>
                  rowKey={(row) => row.event_id?.toString() || `system-${row.event_name}`}
                  pagination={false}
                  dataSource={per_event}
                  scroll={{ x: 720 }}
                  columns={[
                    {
                      title: 'Event',
                      dataIndex: 'event_name',
                      key: 'event_name',
                      render: (value: string) => <Text>{value}</Text>,
                    },
                    {
                      title: 'Sent',
                      dataIndex: 'accepted_count',
                      key: 'accepted_count',
                      align: 'right',
                      render: (value: number) => value.toLocaleString(),
                    },
                    {
                      title: 'Failed',
                      dataIndex: 'failed_count',
                      key: 'failed_count',
                      align: 'right',
                      render: (value: number) => (
                        <Text type={value > 0 ? 'danger' : 'secondary'}>{value.toLocaleString()}</Text>
                      ),
                    },
                    {
                      title: 'Attempts',
                      dataIndex: 'attempt_count',
                      key: 'attempt_count',
                      align: 'right',
                      render: (value: number) => value.toLocaleString(),
                    },
                    {
                      title: 'Last Attempt',
                      dataIndex: 'last_attempted_at',
                      key: 'last_attempted_at',
                      render: formatDateTime,
                    },
                  ]}
                />
              </Card>
            </Col>

            <Col xs={24} xl={8}>
              <Card title="By Email Type" className="h-full">
                <Table<TypeSummary>
                  rowKey="email_type"
                  pagination={false}
                  dataSource={per_type}
                  columns={[
                    {
                      title: 'Type',
                      dataIndex: 'email_type_label',
                      key: 'email_type_label',
                    },
                    {
                      title: 'Sent',
                      dataIndex: 'accepted_count',
                      key: 'accepted_count',
                      align: 'right',
                      render: (value: number) => value.toLocaleString(),
                    },
                    {
                      title: 'Failed',
                      dataIndex: 'failed_count',
                      key: 'failed_count',
                      align: 'right',
                      render: (value: number) => (
                        <Text type={value > 0 ? 'danger' : 'secondary'}>{value.toLocaleString()}</Text>
                      ),
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Recent Attempts" extra={<Text type="secondary">Latest 50 matching records</Text>}>
            <Table<RecentActivity>
              rowKey="id"
              dataSource={recent_activity}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              scroll={{ x: 1180 }}
              columns={[
                {
                  title: 'Time',
                  dataIndex: 'attempted_at',
                  key: 'attempted_at',
                  width: 180,
                  render: formatDateTime,
                },
                {
                  title: 'Event',
                  dataIndex: 'event_name',
                  key: 'event_name',
                  width: 180,
                },
                {
                  title: 'Type',
                  dataIndex: 'email_type_label',
                  key: 'email_type_label',
                  width: 180,
                },
                {
                  title: 'Recipient',
                  dataIndex: 'recipient_email',
                  key: 'recipient_email',
                  width: 220,
                },
                {
                  title: 'Sent By',
                  dataIndex: 'sender',
                  key: 'sender',
                  width: 160,
                },
                {
                  title: 'Subject',
                  dataIndex: 'subject',
                  key: 'subject',
                  width: 220,
                  ellipsis: true,
                  render: (value: string | null) => value || '—',
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  width: 110,
                  render: (value: RecentActivity['status']) => (
                    <Tag
                      color={value === 'accepted' ? 'success' : 'error'}
                      icon={value === 'accepted' ? <CheckCircleOutlined /> : <WarningOutlined />}
                    >
                      {value === 'accepted' ? 'Accepted' : 'Failed'}
                    </Tag>
                  ),
                },
                {
                  title: 'Details',
                  dataIndex: 'detail',
                  key: 'detail',
                  width: 240,
                  ellipsis: true,
                  render: (value: string | null) => value || '—',
                },
              ]}
            />
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}
